import { z } from 'zod';
import { TimesheetSchema } from '../timesheets/types';

// A live timer is only ever running or paused — commit and discard both
// hard-delete the row (no submitted/discarded tombstone).
export const TIMER_STATUSES = ['running', 'paused'] as const;
export type TimerStatus = (typeof TIMER_STATUSES)[number];

export const TimerSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  customerId: z.string().uuid().nullable(),
  productId: z.string().uuid().nullable(),
  // The engagement the user picked when 2+ cover the timer's (customer,
  // workDate, activity); null otherwise — commit binds a single one itself.
  engagementId: z.string().uuid().nullable(),
  workDate: z.string(),
  description: z.string().nullable(),
  billable: z.boolean(),
  status: z.enum(TIMER_STATUSES),
  parentTimerId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Timer = z.infer<typeof TimerSchema>;

export const TimerIntervalSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  timerId: z.string().uuid(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type TimerInterval = z.infer<typeof TimerIntervalSchema>;

/** Timer + computed elapsed seconds. Used in both the open-list response
 *  and the single-timer mutation responses. */
export interface TimerView {
  timer: Timer;
  elapsedSeconds: number;
}

/** Response body of `GET /api/timers/open`. The running timer (≤1) is
 *  promoted to its own field so the widget doesn't have to scan the
 *  paused list to find it; the paused list is capped at `pausedLimit`
 *  with `pausedTotal` exposing the un-truncated count for "+N more". */
export interface OpenTimers {
  running: TimerView | null;
  paused: TimerView[];
  pausedTotal: number;
  pausedLimit: number;
}

/** Single-timer GET response — timer + intervals + computed total. */
export interface TimerWithIntervals {
  timer: Timer;
  intervals: TimerInterval[];
  elapsedSeconds: number;
}

/** Commit response — the produced timesheet plus the committed timer's total
 *  elapsed seconds. The timer itself is hard-deleted on commit, so it's no
 *  longer part of the response. */
export interface CommitResult {
  timesheet: import('../timesheets/types').Timesheet;
  elapsedSeconds: number;
}

export const CreateTimerInput = z.object({
  customerId: z.string().uuid().nullable().optional(),
  productId: z.string().uuid().nullable().optional(),
  engagementId: z.string().uuid().nullable().optional(),
  description: z.string().nullable().optional(),
  billable: z.boolean().optional(),
});
export type CreateTimerInput = z.infer<typeof CreateTimerInput>;

export const PatchTimerInput = z.object({
  customerId: z.string().uuid().nullable().optional(),
  productId: z.string().uuid().nullable().optional(),
  // Omitted on a customer/activity change clears the stored pick server-side.
  engagementId: z.string().uuid().nullable().optional(),
  description: z.string().nullable().optional(),
  billable: z.boolean().optional(),
});
export type PatchTimerInput = z.infer<typeof PatchTimerInput>;

// Re-export TimesheetSchema-derived type so consumers don't need to chase imports.
export type { Timesheet } from '../timesheets/types';
export { TimesheetSchema };
